import { type RefObject, useCallback, useEffect } from 'react';

import { useMediaQuery } from '@hrworks/design-system/hooks';
import { Badge } from '@hrworks/sui-core/Badge';
import { FontAwesomeIcon } from '@hrworks/sui-core/FontAwesomeIcon';
import { Text } from '@hrworks/sui-core/Text';
import { useScrollIndicator } from '@hrworks/sui-shared/hooks';

import { S } from './Content.styles';
import type { ContentProps } from './Content.types';
import { PersonCard } from './PersonCard/PersonCard';

type InternalContentProps = ContentProps & {
  onCanScrollDownChange?: (canScrollDown: boolean) => void;
  onCanScrollUpChange?: (canScrollUp: boolean) => void;
  onScrollDirectionChange?: (direction: 'up' | 'down' | null) => void;
  /** Receives the content scroll container, exposed via SqwLayoutContext. */
  scrollerRef?: RefObject<HTMLElement | null>;
};

export const Content = ({
  children,
  borderless,
  breadcrumb,
  personCard,
  onCanScrollDownChange,
  onCanScrollUpChange,
  onScrollDirectionChange,
  scrollerRef,
  ...otherProps
}: InternalContentProps) => {
  const isSmallDevice = useMediaQuery('isSmallDevice');
  const showBreadcrumb = !!(isSmallDevice && breadcrumb);
  const { badgeTitle, ...otherPersonCardProps } = personCard || {};
  const [{ canScrollUp, canScrollDown, scrollDirection }, scrollIndicatorRef] = useScrollIndicator();

  const contentRef = useCallback(
    (element: HTMLDivElement | null) => {
      scrollIndicatorRef(element);
      if (scrollerRef) scrollerRef.current = element;
    },
    [scrollIndicatorRef, scrollerRef],
  );

  useEffect(() => {
    onCanScrollUpChange?.(canScrollUp);
  }, [canScrollUp, onCanScrollUpChange]);

  useEffect(() => {
    onCanScrollDownChange?.(canScrollDown);
  }, [canScrollDown, onCanScrollDownChange]);

  useEffect(() => {
    onScrollDirectionChange?.(scrollDirection);
  }, [scrollDirection, onScrollDirectionChange]);

  return (
    <S.ContentContainer {...otherProps}>
      {showBreadcrumb && (
        <S.Breadcrumb data-shadow={canScrollUp}>
          <S.BreadcrumbButton
            variant="subtle"
            leftIcon={<FontAwesomeIcon name="chevron-left" />}
            onClick={breadcrumb.onClick}
          >
            <Text lineClamp={2}>{breadcrumb.title}</Text>
          </S.BreadcrumbButton>
          {badgeTitle && <Badge badgeContent={badgeTitle} />}
        </S.Breadcrumb>
      )}
      <S.Content ref={contentRef} $borderless={borderless} $showBreadcrumb={showBreadcrumb}>
        {personCard && (
          <S.ContentHeader borderless={borderless} hasBreadcrumb={showBreadcrumb}>
            <PersonCard {...(!isSmallDevice && { badgeTitle })} {...otherPersonCardProps} />
          </S.ContentHeader>
        )}
        <S.Children>{children}</S.Children>
      </S.Content>
    </S.ContentContainer>
  );
};
